import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import securePersist from '@/utils/securePersist';
import type { IProductVariant } from '@/types/api/product/variant';

type CartItem = {
  variant: IProductVariant | null;
  quantity: number | null;
  subscriptionType?: string;
  isRdt: boolean;
  maxQuantity: number | null;
  numberOfVariants: number | null;
  isVariableVariant: boolean;
};

export type CartStore = {
  cart: CartItem | null;
  setVariant: (variant: IProductVariant) => void;
  setQuantity: (quantity: number) => void;
  setSubscriptionType: (subscriptionType: string) => void;
  clearCart: () => void;
  setRdt: (isRdt: boolean) => void;
  setMaxQuantity: (maxQuantity: number) => void;
  setNumberOfVariants: (numberOfVariants: number) => void;
  setIsVariableVariant: (isVariableVariant: boolean) => void;
  setCart: (cartUpdate: Partial<CartItem>) => void;
  reset: () => void;
};

const initialCart: CartItem = {
  variant: null,
  quantity: null,
  subscriptionType: undefined,
  isRdt: false,
  maxQuantity: null,
  numberOfVariants: null,
  isVariableVariant: false,
};

export const useCartStore = create<CartStore>()(
  devtools(
    securePersist(
      (set, get) => ({
        cart: {
          variant: null,
          quantity: null,
          subscriptionType: undefined,
          isRdt: false,
          maxQuantity: null,
          numberOfVariants: null,
          isVariableVariant: false,
        },
        setVariant: (variant) =>
          set(
            (state) => (state.cart ? { cart: { ...state.cart, variant } } : state),
            false,
            'SET_VARIANT',
          ),

        setQuantity: (quantity) =>
          set(
            (state) => (state.cart ? { cart: { ...state.cart, quantity } } : state),
            false,
            'SET_QUANTITY',
          ),

        setSubscriptionType: (subscriptionType) =>
          set(
            (state) => (state.cart ? { cart: { ...state.cart, subscriptionType } } : state),
            false,
            'SET_SUBSCRIPTION_TYPE',
          ),

        clearCart: () =>
          set(
            {
              cart: {
                variant: null,
                quantity: null,
                subscriptionType: undefined,
                isRdt: false,
                maxQuantity: null,
                numberOfVariants: null,
                isVariableVariant: false,
              },
            },
            false,
            'CLEAR_CART',
          ),

        setRdt: (isRdt) =>
          set(
            (state) => (state.cart ? { cart: { ...state.cart, isRdt } } : state),
            false,
            'SET_RDT',
          ),

        setMaxQuantity: (maxQuantity) =>
          set(
            (state) => (state.cart ? { cart: { ...state.cart, maxQuantity } } : state),
            false,
            'SET_MAX_QUANTITY',
          ),

        setNumberOfVariants: (numberOfVariants) =>
          set(
            (state) => (state.cart ? { cart: { ...state.cart, numberOfVariants } } : state),
            false,
            'SET_NUMBER_OF_VARIANTS',
          ),

        setIsVariableVariant: (isVariableVariant) =>
          set(
            (state) => (state.cart ? { cart: { ...state.cart, isVariableVariant } } : state),
            false,
            'SET_IS_VARIABLE_VARIANT',
          ),

        setCart: (cartUpdate: Partial<CartItem>) =>
          set(
            (state) => ({
              cart: { ...state.cart!, ...cartUpdate } as CartItem,
            }),
            false,
            'SET_CART',
          ),

        reset: () => set({ cart: { ...initialCart } }, false, 'RESET_CART'),

        totalQuantity: () => {
          const cart = get().cart;
          return cart ? cart.quantity : null;
        },
      }),
      { name: 'cart-storage' },
    ),
  ),
);
